import type { CSSProperties } from "react";
import logoWhite from "../../../assets/wembii-logo-white.png";
import type { SupportChatAppearance } from "../../../cores/wembiiSupport/interfaces/types";
import {
  SUPPORT_CHAT_RAMIRO_AVATAR,
  supportChatPublicLogoUrl,
} from "./supportChatTeamAvatars";

export const defaultSupportAppearance: SupportChatAppearance = {
  widgetTitle: "Soporte Wembii",
  welcomeTitle: "Hola, ¿cómo te ayudamos hoy?",
  welcomeMessage:
    "Escríbenos y un especialista de Wembii te responderá en breve.",
  inputPlaceholder: "Escribe tu mensaje...",
  ctaLabel: "Iniciar conversación",
  onlineLabel: "Normalmente responde en menos de 15 minutos",
  headerColor: "#8b5cf6",
  bubbleColor: "#f1f5f9",
  widgetBg: "#ffffff",
  textColor: "#0f172a",
  accentColor: "#7c3aed",
  position: "right",
  avatarType: "logo",
  showAgentName: true,
  showTimestamp: true,
  roundedStyle: "soft",
  showQuickButtons: true,
  showCommercialQuickButton: true,
  showTechnicalQuickButton: true,
  quickButtonCommercial: "Consulta comercial",
  quickButtonTechnical: "Consulta técnica",
};

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function parseHex(hex: string): [number, number, number] | null {
  const raw = hex.replace("#", "").trim();
  if (!/^[0-9a-fA-F]{3}$|^[0-9a-fA-F]{6}$/.test(raw)) return null;
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((char) => char + char)
          .join("")
      : raw;
  const value = parseInt(full, 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toHex([r, g, b]: [number, number, number]) {
  return `#${[r, g, b]
    .map((channel) => clamp(Math.round(channel), 0, 255).toString(16).padStart(2, "0"))
    .join("")}`;
}

export function darkenHex(hex: string, amount: number) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  const factor = 1 - clamp(amount, 0, 1);
  return toHex(rgb.map((channel) => channel * factor) as [number, number, number]);
}

export function mixHex(hexA: string, hexB: string, weight: number) {
  const a = parseHex(hexA);
  const b = parseHex(hexB);
  if (!a || !b) return hexA;
  const w = clamp(weight, 0, 1);
  return toHex(
    a.map((channel, index) => channel * (1 - w) + b[index]! * w) as [
      number,
      number,
      number,
    ]
  );
}

export function hexAlpha(hex: string, alpha: number) {
  const rgb = parseHex(hex);
  if (!rgb) return hex;
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${clamp(alpha, 0, 1)})`;
}

export function mergeSupportAppearance(
  config?: Partial<SupportChatAppearance> | null
): SupportChatAppearance {
  return { ...defaultSupportAppearance, ...config };
}

export type SupportChatTheme = {
  appearance: SupportChatAppearance;
  cssVars: CSSProperties;
  panelRoundedClass: string;
  bubbleRoundedClass: string;
  position: "left" | "right";
};

const PANEL_ROUNDED: Record<SupportChatAppearance["roundedStyle"], string> = {
  soft: "wsc-panel-inner--rounded-soft",
  rounded: "wsc-panel-inner--rounded-rounded",
  square: "wsc-panel-inner--rounded-square",
};

const BUBBLE_ROUNDED: Record<SupportChatAppearance["roundedStyle"], string> = {
  soft: "wsc-bubble--rounded-soft",
  rounded: "wsc-bubble--rounded-rounded",
  square: "wsc-bubble--rounded-square",
};

export function resolveSupportChatTheme(
  config?: Partial<SupportChatAppearance> | null
): SupportChatTheme {
  const appearance = mergeSupportAppearance(config);
  const accent = appearance.accentColor;
  const header = appearance.headerColor;

  const cssVars = {
    "--wsc-accent": accent,
    "--wsc-accent-deep": darkenHex(accent, 0.14),
    "--wsc-header-solid": header,
    "--wsc-pill-active": hexAlpha(darkenHex(header, 0.25), 0.55),
    "--wsc-brand-circle": darkenHex(accent, 0.1),
    "--wsc-glow-strong": hexAlpha(accent, 0.45),
    "--wsc-glow-stronger": hexAlpha(accent, 0.52),
    "--wsc-ring-border": hexAlpha(accent, 0.42),
    "--wsc-send-hover-bg": mixHex(accent, "#ffffff", 0.92),
    "--wsc-bubble-out-bg": mixHex(accent, "#ffffff", 0.78),
    "--wsc-bubble-in-bg": appearance.bubbleColor,
    "--wsc-widget-bg": appearance.widgetBg,
    "--wsc-text": appearance.textColor,
    "--wsc-border-subtle": hexAlpha(appearance.textColor, 0.12),
    "--wsc-composer-bg": appearance.widgetBg,
    "--wsc-muted-text": hexAlpha(appearance.textColor, 0.55),
    "--wsc-chip-border": hexAlpha(accent, 0.35),
    "--wsc-chip-text": darkenHex(accent, 0.08),
    "--wsc-chip-hover-bg": mixHex(accent, "#ffffff", 0.94),
  } as CSSProperties;

  return {
    appearance,
    cssVars,
    panelRoundedClass: PANEL_ROUNDED[appearance.roundedStyle],
    bubbleRoundedClass: BUBBLE_ROUNDED[appearance.roundedStyle],
    position: appearance.position,
  };
}

/** Avatar en burbujas de agente / bienvenida. */
export function resolveSupportMessageAvatar(
  avatarType: SupportChatAppearance["avatarType"],
  senderAvatar?: string | null
) {
  if (avatarType === "agent") {
    return senderAvatar || SUPPORT_CHAT_RAMIRO_AVATAR;
  }
  return logoWhite;
}

/** Logo del disco de marca en el encabezado del chat. */
export function resolveSupportBrandAvatar(
  avatarType: SupportChatAppearance["avatarType"],
  customLogo?: string | null
) {
  if (avatarType === "agent") {
    return SUPPORT_CHAT_RAMIRO_AVATAR;
  }
  const custom = customLogo?.trim();
  return custom || supportChatPublicLogoUrl();
}
